import { useState, useEffect, useRef, useCallback } from "react";
import { useForm } from "react-hook-form";
import { IMaskInput } from "react-imask";
import { Link, useNavigate } from "react-router-dom";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useModal } from "../../hooks/useModalHook";
import { useExitIntentLead, EXIT_INTENT_CONFIG } from "../../hooks/useExitIntentLead";
import { resetDocumentScrollToTop } from "../../utils/resetDocumentScrollToTop";
import lidIntentTop from "../../images/lid_intent_top.webp";
import lidIntentTop2x from "../../images/lid_intent_top_x2.webp";
import lidIntent from "../../images/lid_intent.webp";
import lidIntent2x from "../../images/lid_intent_x2.webp";
import lidIntentSquare from "../../images/lid_intent_square.webp";
import styles from "./ExitIntentLead.module.css";

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const BUBBLE_SIZE = 100;
const BUBBLE_MARGIN = 16;
const COLLAPSE_DURATION_MS = 600;
const PRELOAD_IMAGES = [
  lidIntentTop,
  lidIntentTop2x,
  lidIntent,
  lidIntent2x,
  lidIntentSquare,
];

const ExitIntentLead = () => {
  const { modal } = useModal();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const navigate = useNavigate();
  const [view, setView] = useState("form");
  const [isCollapsing, setIsCollapsing] = useState(false);
  const { isActive, isOpen, isMinimized, close, minimize, expand } = useExitIntentLead({
    isModalOpen: modal.open,
    isCollapsing,
  });
  const [landedFromCollapse, setLandedFromCollapse] = useState(false);
  const [formError, setFormError] = useState("");
  const [phoneMask, setPhoneMask] = useState("");
  const dialogRef = useRef(null);
  const successAutoCloseRef = useRef(null);
  const collapseFallbackRef = useRef(null);
  const collapseFinishedRef = useRef(false);
  const collapseTransitionHandlerRef = useRef(null);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { isSubmitting, errors, isSubmitted },
  } = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: { name: "", phone: "", email: "", consent: false },
  });

  useEffect(() => {
    PRELOAD_IMAGES.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  const resetView = useCallback(() => {
    setView("form");
    setFormError("");
    reset();
    setPhoneMask("");
  }, [reset]);

  useEffect(() => {
    if (!isActive) {
      resetView();
      setIsCollapsing(false);
      setLandedFromCollapse(false);
      if (successAutoCloseRef.current) {
        clearTimeout(successAutoCloseRef.current);
        successAutoCloseRef.current = null;
      }
      return;
    }
    if (isMinimized || isCollapsing) return;
    const t = setTimeout(
      () => document.getElementById("exit-intent-lead-name")?.focus(),
      EXIT_INTENT_CONFIG.transitionMs
    );
    return () => clearTimeout(t);
  }, [isActive, isMinimized, isCollapsing, resetView]);

  const clearCollapseStyles = useCallback(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    dialog.style.removeProperty("--collapse-x");
    dialog.style.removeProperty("--collapse-y");
    dialog.style.removeProperty("width");
    dialog.style.removeProperty("height");
    dialog.style.removeProperty("max-width");
    dialog.style.removeProperty("max-height");
  }, []);

  const clearCollapseFallback = useCallback(() => {
    if (collapseFallbackRef.current !== null) {
      clearTimeout(collapseFallbackRef.current);
      collapseFallbackRef.current = null;
    }
  }, []);

  const abortCollapse = useCallback(() => {
    clearCollapseFallback();
    const dialog = dialogRef.current;
    if (dialog && collapseTransitionHandlerRef.current) {
      dialog.removeEventListener("transitionend", collapseTransitionHandlerRef.current);
      collapseTransitionHandlerRef.current = null;
    }
    clearCollapseStyles();
    setIsCollapsing(false);
    collapseFinishedRef.current = false;
  }, [clearCollapseFallback, clearCollapseStyles]);

  const handleDismiss = useCallback(
    (rememberDismiss = true) => {
      if (isCollapsing) return;
      abortCollapse();
      close(rememberDismiss);
    },
    [isCollapsing, abortCollapse, close]
  );

  const goToAgreement = useCallback(() => {
    resetDocumentScrollToTop();
    navigate("/polzovatelskoe-soglashenie");
  }, [navigate]);

  const finishCollapse = useCallback(() => {
    if (collapseFinishedRef.current) return;
    collapseFinishedRef.current = true;
    clearCollapseFallback();
    clearCollapseStyles();
    setIsCollapsing(false);
    setLandedFromCollapse(true);
    minimize();
    goToAgreement();
  }, [clearCollapseFallback, clearCollapseStyles, minimize, goToAgreement]);

  const handleConsentLinkClick = (e) => {
    e.preventDefault();
    if (isCollapsing) return;
    if (!isOpen) {
      goToAgreement();
      return;
    }

    const dialog = dialogRef.current;
    if (!dialog) {
      minimize();
      goToAgreement();
      return;
    }

    const rect = dialog.getBoundingClientRect();
    const marginLeft = BUBBLE_MARGIN;
    const marginBottom = BUBBLE_MARGIN;
    const targetCenterX = marginLeft + BUBBLE_SIZE / 2;
    const targetCenterY = window.innerHeight - marginBottom - BUBBLE_SIZE / 2;
    const originX = rect.left + rect.width / 2;
    const originY = rect.top + rect.height / 2;

    dialog.style.width = `${rect.width}px`;
    dialog.style.height = `${rect.height}px`;
    dialog.style.maxWidth = "none";
    dialog.style.maxHeight = "none";
    dialog.style.setProperty("--collapse-x", `${targetCenterX - originX}px`);
    dialog.style.setProperty("--collapse-y", `${targetCenterY - originY}px`);

    collapseFinishedRef.current = false;

    const onTransitionEnd = (ev) => {
      if (ev.target !== dialog || ev.propertyName !== "transform") return;
      dialog.removeEventListener("transitionend", onTransitionEnd);
      collapseTransitionHandlerRef.current = null;
      finishCollapse();
    };
    collapseTransitionHandlerRef.current = onTransitionEnd;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsCollapsing(true);
        dialog.addEventListener("transitionend", onTransitionEnd);
        clearCollapseFallback();
        collapseFallbackRef.current = window.setTimeout(() => {
          dialog.removeEventListener("transitionend", onTransitionEnd);
          collapseTransitionHandlerRef.current = null;
          finishCollapse();
        }, COLLAPSE_DURATION_MS + 80);
      });
    });
  };

  const handleExpand = () => {
    setLandedFromCollapse(false);
    expand();
  };

  const showSuccess = useCallback(() => {
    setView("success");
    if (successAutoCloseRef.current) clearTimeout(successAutoCloseRef.current);
    successAutoCloseRef.current = setTimeout(() => {
      handleDismiss(true);
    }, 5000);
  }, [handleDismiss]);

  const onSubmit = async (data) => {
    if (!executeRecaptcha) {
      setFormError("Сервис защиты временно недоступен. Попробуйте позже.");
      return;
    }
    setFormError("");
    try {
      const token = await executeRecaptcha("submit");
      const res = await fetch("/sendLeadCapture.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: data.name,
          phone: data.phone,
          email: data.email?.trim() ?? "",
          recaptchaToken: token,
        }),
      });
      let json;
      try {
        json = await res.json();
      } catch {
        throw new Error("Сервер вернул неожиданный ответ");
      }
      if (!res.ok || json.status !== "success") {
        throw new Error(json.message || `HTTP ${res.status}`);
      }
      window.ym?.(9956731, "reachGoal", "zapis_form_lid_zahvat");
      showSuccess();
    } catch (err) {
      setFormError(err.message || "Ошибка отправки. Попробуйте позже.");
    }
  };

  const trapFocus = useCallback(
    (e) => {
      if (!isOpen || isMinimized || e.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (el) => el.offsetParent !== null
      );
      if (!focusable.length) return;
      if (!dialogRef.current.contains(document.activeElement)) {
        e.preventDefault();
        focusable[0].focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [isOpen, isMinimized]
  );

  useEffect(() => {
    document.addEventListener("keydown", trapFocus);
    return () => document.removeEventListener("keydown", trapFocus);
  }, [trapFocus]);

  useEffect(() => {
    if (!modal.open) return;
    abortCollapse();
  }, [modal.open, abortCollapse]);

  useEffect(() => {
    return () => {
      if (successAutoCloseRef.current) clearTimeout(successAutoCloseRef.current);
      abortCollapse();
    };
  }, [abortCollapse]);

  const rootClass = [
    styles.root,
    isOpen || isCollapsing ? styles.open : "",
    isCollapsing ? styles.collapsing : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (!isActive) {
    return null;
  }

  if (isMinimized) {
    return (
      <button
        type="button"
        className={`${styles.minimized}${landedFromCollapse ? ` ${styles.minimizedLanded}` : ""}`}
        onClick={handleExpand}
        aria-label="Развернуть форму записи"
        title="Продолжить запись"
      >
        <img
          className={styles.minimizedImage}
          src={lidIntentSquare}
          alt=""
          width={100}
          height={100}
        />
      </button>
    );
  }

  return (
    <div
      id="exit-intent-lead"
      className={rootClass}
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-intent-lead-title"
      aria-hidden={!isOpen && !isCollapsing}
    >
      <div
        className={styles.overlay}
        tabIndex={-1}
        aria-hidden="true"
        onClick={() => handleDismiss(true)}
      />
      <div ref={dialogRef} className={styles.dialog}>
        <div className={styles.collapsePreview} aria-hidden="true">
          <img
            className={styles.collapsePreviewImage}
            src={lidIntentSquare}
            alt=""
          />
        </div>
        <button
          type="button"
          className={styles.close}
          aria-label="Закрыть"
          onClick={() => handleDismiss(true)}
        >
          <span aria-hidden="true">×</span>
        </button>
        <div className={styles.inner}>
          <div className={styles.formCol}>
            <div className={styles.formWrap}>
              <img
                className={styles.topImage}
                src={lidIntentTop}
                srcSet={`${lidIntentTop} 1x, ${lidIntentTop2x} 2x`}
                alt=""
                width={900}
                loading="eager"
                decoding="async"
                fetchPriority="high"
              />
              <h2 id="exit-intent-lead-title" className={styles.title}>
                Получите бесплатный приём репродуктолога
              </h2>

              {view === "form" ? (
                <form
                  className={styles.form}
                  onSubmit={handleSubmit(onSubmit)}
                  noValidate
                >
                  <div
                    className={
                      isSubmitted && errors.name
                        ? `${styles.inputWrap} ${styles.inputWrapError}`
                        : styles.inputWrap
                    }
                  >
                    <label className={styles.visuallyHidden} htmlFor="exit-intent-lead-name">
                      Ваше имя
                    </label>
                    <input
                      className={styles.input}
                      type="text"
                      id="exit-intent-lead-name"
                      placeholder="Ваше имя"
                      autoComplete="name"
                      {...register("name", { required: "Введите имя" })}
                    />
                    <span className={styles.inputLabel}>Ваше имя</span>
                  </div>
                  {isSubmitted && errors.name && (
                    <span className={styles.fieldError} role="alert">
                      {errors.name.message}
                    </span>
                  )}

                  <div
                    className={
                      isSubmitted && errors.phone
                        ? `${styles.inputWrap} ${styles.inputWrapError}`
                        : styles.inputWrap
                    }
                  >
                    <label className={styles.visuallyHidden} htmlFor="exit-intent-lead-phone">
                      Телефон
                    </label>
                    <IMaskInput
                      mask="+7 (000) 000-00-00"
                      definitions={{ 0: /\d/ }}
                      className={styles.input}
                      type="tel"
                      id="exit-intent-lead-phone"
                      inputMode="tel"
                      placeholder="Тел. +7 (___) ___-__-__"
                      autoComplete="tel"
                      value={phoneMask}
                      onAccept={(val) => {
                        setPhoneMask(val);
                        setValue("phone", val, { shouldValidate: isSubmitted });
                      }}
                      {...register("phone", {
                        required: "Введите номер телефона",
                        pattern: {
                          value: /^\+7 \(\d{3}\) \d{3}-\d{2}-\d{2}$/,
                          message: "Введите корректный номер телефона",
                        },
                      })}
                    />
                    <span className={styles.inputLabel}>Телефон</span>
                  </div>
                  {isSubmitted && errors.phone && (
                    <span className={styles.fieldError} role="alert">
                      {errors.phone.message}
                    </span>
                  )}

                  <div
                    className={
                      isSubmitted && errors.email
                        ? `${styles.inputWrap} ${styles.inputWrapError}`
                        : styles.inputWrap
                    }
                  >
                    <label className={styles.visuallyHidden} htmlFor="exit-intent-lead-email">
                      E-mail
                    </label>
                    <input
                      className={styles.input}
                      type="email"
                      id="exit-intent-lead-email"
                      placeholder="E-mail"
                      autoComplete="email"
                      inputMode="email"
                      {...register("email", {
                        required: "Введите email",
                        pattern: {
                          value: /^\S+@\S+\.\S+$/,
                          message: "Введите корректный email",
                        },
                      })}
                    />
                    <span className={styles.inputLabel}>E-mail</span>
                  </div>
                  {isSubmitted && errors.email && (
                    <span className={styles.fieldError} role="alert">
                      {errors.email.message}
                    </span>
                  )}

                  <div
                    className={
                      isSubmitted && errors.consent
                        ? `${styles.agree} ${styles.agreeError}`
                        : styles.agree
                    }
                  >
                    <div className={styles.agreeContent}>
                      <label>
                        <input
                          type="checkbox"
                          id="exit-intent-lead-agree"
                          className={styles.checkboxInput}
                          {...register("consent", {
                            required: "Необходимо согласие на обработку персональных данных",
                          })}
                        />
                      </label>
                      <span className={styles.agreeText}>
                        Нажимая кнопку «Записаться», я принимаю{" "}
                        <Link
                          className={styles.consentLink}
                          to="/polzovatelskoe-soglashenie"
                          onClick={(e) => {
                            resetDocumentScrollToTop();
                            handleConsentLinkClick(e);
                          }}
                        >
                          условия обработки информации
                        </Link>
                      </span>
                    </div>
                    {isSubmitted && errors.consent && (
                      <span className={styles.fieldError} role="alert">
                        {errors.consent.message}
                      </span>
                    )}
                  </div>

                  <div className={styles.formButtonActionWrapper}>
                    <button
                      type="submit"
                      className={styles.formButtonAction}
                      disabled={isSubmitting}
                    >
                      <span className={styles.formButtonSpan}>
                        {isSubmitting ? "Отправка…" : "Записаться"}
                      </span>
                      <span className={styles.formCircle} aria-hidden="true">
                        <span className={styles.formInnerCircle}></span>
                      </span>
                    </button>
                  </div>

                  {formError && (
                    <p className={styles.formError} role="alert">
                      {formError}
                    </p>
                  )}

                  <p className={styles.note}>
                    * Для новых пациентов к врачам: Волкова, Лисова, Хачатурян, Черемных, Ладыгина.
                  </p>
                </form>
              ) : (
                <div className={styles.success}>
                  <p className={styles.successText}>Спасибо! Мы скоро свяжемся с вами.</p>
                  <button
                    type="button"
                    className={styles.successClose}
                    onClick={() => handleDismiss(true)}
                  >
                    Закрыть
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className={styles.mediaCol} aria-hidden="true">
            <img
              className={styles.image}
              src={lidIntent}
              srcSet={`${lidIntent} 1x, ${lidIntent2x} 2x`}
              alt=""
              width={450}
              height={700}
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExitIntentLead;
